import { useEffect, useRef, useState } from "react";

/**
 * The title of a row, while it is being rewritten in place.
 *
 * Enter saves, Escape cancels, clicking away saves — the three things a field
 * like this is expected to do without being told. Blank text and text that came
 * back unchanged both count as a cancel: neither is worth a write.
 *
 * `done` is not a nicety. Escape and Enter each unmount the input, and
 * unmounting fires `blur` — so without the flag every cancel would be chased by
 * the save it had just refused.
 *
 * Shared by the task panel and the goals list: both open it by double-clicking
 * a title or through «Редагувати» in «⋯», and a title that saves one way in one
 * list and another way in the other is a rule the user has to learn twice.
 */
export default function InlineTitleEditor({
  initial,
  label,
  onDone,
  className = "py-0.5 text-sm",
}: {
  initial: string;
  /** `aria-label` for the field — say which row this is. */
  label: string;
  /** The new title, or `null` for "leave the row as it was". */
  onDone: (value: string | null) => void;
  className?: string;
}) {
  const [text, setText] = useState(initial);
  const field = useRef<HTMLInputElement>(null);
  const done = useRef(false);

  // Selected, not just focused: the common edit is a rewrite, and the common
  // correction is a word at the end — one of which needs a keystroke either way.
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);

  const finish = (value: string | null) => {
    if (done.current) return;
    done.current = true;
    const next = value?.trim() ?? "";
    onDone(value !== null && next !== "" && next !== initial.trim() ? next : null);
  };

  return (
    <form
      className="min-w-0 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        finish(text);
      }}
    >
      <input
        ref={field}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          finish(null);
        }}
        onBlur={() => finish(text)}
        aria-label={label}
        className={`jv-input w-full ${className}`}
      />
    </form>
  );
}
